<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="page">
    <!-- 顶部导航区：返回、标题、提示、分享 -->
    <div class="header">
      <div class="back">
        <img src="@/assets/report/back.png" alt="" />
      </div>
      <div class="header-title">词汇量测评报告</div>

      <div class="tips">
        根据您的测试情况，我们已经为您生成了个性化测评报告，并已保存至【精准测评】模块供后续查看
      </div>

      <!-- <div class="share">
        <img src="@/assets/report/share.png" alt="" />
      </div> -->
    </div>

    <!-- 报告内容区 -->
    <div class="box">
      <!-- 核心数据概览：词汇量、题数、正确率 -->
      <FadeInSection>
        <ReportTitle title="核心数据概览" />
        <DataOverview
          :masteredWord="masteredWord"
          :leveRange="leveRange"
          :questions="questions"
          :right-num="rightNum"
          :exam-time="examTime"
          :exam-time-display="examTimeDisplay"
          :right-rate="rightRate"
          :avgReactionTime="avgReactionTime"
        />
      </FadeInSection>

      <DividerAnimated />

      <FadeInSection>
        <ReportTitle title="级别定位" />
        <LevelOverview
          :masteryName="stageName"
          :masterDesc="masterDesc"
          :levelMasteryRates="levelMasteryRates"
        />
      </FadeInSection>

      <DividerAnimated />

      <FadeInSection>
        <ReportTitle title="主题能力分析" />
        <TopicAbility
          :userClassifyMastery="userClassifyMastery"
          :classifyMasteryRates="classifyMasteryRates"
        />
      </FadeInSection>

      <DividerAnimated style="margin-top: -30px" />

      <FadeInSection>
        <ReportTitle title="错词回顾" />
        <WrongWordReview :errorWords="errorWords" />
      </FadeInSection>

      <DividerAnimated />

      <FadeInSection>
        <ReportTitle title="老师建议" />
        <TeacherAdvice
          :teacherAdvice="teacherAdvice"
          :aiSubmitContent="aiSubmitContent"
          :ai-token="aiToken"
          :report-id="preciseReportId"
          :skip-ai-stream="skipTeacherAiStream"
        />
      </FadeInSection>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { setDesignSize } from "@/utils/adapt.js";
import ReportTitle from "./components/ReportTitle.vue";

import DataOverview from "./components/DataOverview.vue";
import FadeInSection from "./components/FadeInSection.vue";
import DividerAnimated from "./components/DividerAnimated.vue";
import LevelOverview from "./components/LevelOverview.vue";
import TopicAbility from "./components/TopicAbility.vue";
import WrongWordReview from "./components/WrongWordReview.vue";
import TeacherAdvice from "./components/TeacherAdvice.vue";

import { getPreciseReport } from "@/api/precise.js";
import { aiAuth } from "@/api/chat.js";

const report = ref(null);
const aiToken = ref("");

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatExamTime = (timeVal) => {
  const t = safeNumber(timeVal);
  if (t === null) return undefined;
  let sec = Math.floor(t / 1000);
  if (sec <= 0 && t > 0 && t >= 30 && t <= 7200) {
    sec = Math.floor(t);
  }
  if (sec <= 0) return "0秒";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m <= 0) return `${sec}秒`;
  return `${m}分${s}秒`;
};

const toPercent = (v) => {
  const n = safeNumber(v);
  if (n === null) return 0;
  if (n <= 1) return Math.round(n * 100);
  if (n > 100 && n <= 1e4) return Math.round(n);
  return Math.round(n);
};

const getLabel = (item) =>
  item?.label ?? item?.name ?? item?.classifyName ?? item?.topicName ?? item?.levelName ?? item?.rangeName ?? "";

const mapTopicRates = (list) => {
  if (!Array.isArray(list)) return undefined;
  const mapped = list
    .map((item) => ({
      label: getLabel(item) || "主题",
      percent: toPercent(item.percent ?? item.rate ?? item.value ?? item.masteryRate),
    }))
    .filter((x) => x && x.label);
  return mapped;
};

const mapWrongWords = (list) => {
  if (!Array.isArray(list)) return undefined;
  return list.map((item) => ({
    word: item.wordName ?? item.word ?? "",
    pos: item.pos ?? item.partOfSpeech ?? "",
    meaning: item.translate ?? item.content ?? item.meaning ?? item.trans ?? "",
  }));
};

const masteredWord = computed(() => {
  const r = report.value;
  if (!r || r.masteredWord === undefined || r.masteredWord === null) return 0;
  return r.masteredWord;
});

const leveRange = computed(() => {
  const r = report.value;
  if (!r) return "";
  const v = r.leveRange ?? r.masteredWordRange;
  if (v === undefined || v === null || String(v).trim() === "") return "";
  return String(v);
});

const questions = computed(() => {
  const r = report.value;
  if (!r || r.questions === undefined || r.questions === null) return 0;
  return r.questions;
});

const rightNum = computed(() => {
  const r = report.value;
  if (!r) return undefined;
  if (r.rightNum === undefined || r.rightNum === null) return undefined;
  return r.rightNum;
});

const examTime = computed(() => {
  const r = report.value;
  if (!r) return undefined;
  if (r.examTime === undefined || r.examTime === null) return undefined;
  return r.examTime;
});

const examTimeDisplay = computed(() => {
  const r = report.value;
  if (!r) return "0秒";
  return formatExamTime(r.examTime) ?? "0秒";
});

const rightRate = computed(() => {
  const r = report.value;
  if (!r) return 0;
  if (r.rightRate !== undefined && r.rightRate !== null && String(r.rightRate).trim() !== "") {
    const n = safeNumber(r.rightRate);
    if (n === null) return 0;
    return n <= 1 && n > 0 ? Math.round(n * 100) : Math.round(n);
  }
  if (r.questions === undefined || r.questions === null || r.rightNum === undefined || r.rightNum === null) {
    return 0;
  }
  const q = safeNumber(r.questions);
  const n = safeNumber(r.rightNum);
  if (q === null || q <= 0) return 0;
  if (n === null) return 0;
  return Math.round((n / q) * 100);
});

const avgReactionTime = computed(() => {
  const r = report.value;
  if (!r) return "0";
  const v = safeNumber(r.avgReactionTime ?? r.averageReactionTime);
  return v === null ? "0" : String(v);
});

const stageName = computed(() => {
  const r = report.value;
  if (!r) return "";
  const name = r.stageName;
  if (name !== undefined && name !== null && String(name).trim() !== "") {
    return String(name).trim();
  }
  const el = r.examLevel;
  if (el !== undefined && el !== null && String(el).trim() !== "") {
    const s = String(el).trim();
    if (/^L\d+/i.test(s)) return s;
    const n = safeNumber(el);
    return n !== null ? `L${n}` : s;
  }
  return "";
});

const masterDesc = computed(() => {
  const r = report.value;
  if (!r) return "";
  const v = r.masterDesc ?? r.desc;
  if (v === undefined || v === null || String(v).trim() === "") return "";
  return String(v);
});

const levelMasteryRates = computed(() => {
  const list = report.value?.levelMasteryRates;
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((item, idx) => {
    const ln = item.levelName;
    const fromName =
      ln != null && String(ln).trim() !== "" ? String(ln).trim() : "";
    const fromLeve =
      item.leve != null && item.leve !== "" ? `L${item.leve}` : "";
    const levelName = fromName || fromLeve || getLabel(item) || `L${idx + 1}`;
    const masterName = item.masterName != null ? String(item.masterName) : "";
    const pct = toPercent(item.masteryRate ?? item.percent ?? item.rate ?? item.value);
    return {
      levelName,
      masterName,
      masteryRate: pct,
      percent: pct,
    };
  });
});

const userClassifyMastery = computed(() => {
  const mapped = mapTopicRates(report.value?.userClassifyMastery);
  return mapped && mapped.length > 0 ? mapped : [];
});

const classifyMasteryRates = computed(() => {
  const mapped = mapTopicRates(report.value?.classifyMasteryRates);
  return mapped && mapped.length > 0 ? mapped : [];
});

const errorWords = computed(() => {
  const r = report.value;
  if (!r || r.errorWords === undefined || r.errorWords === null) return [];
  return mapWrongWords(r.errorWords) ?? [];
});

const teacherAdvice = computed(() => {
  const r = report.value;
  if (!r) return "";
  const fromAnalysis = r.analysis;
  if (fromAnalysis !== undefined && fromAnalysis !== null && String(fromAnalysis).trim() !== "") {
    return String(fromAnalysis).trim();
  }
  const v = r.teacherAdvice ?? r.advice;
  if (v === undefined || v === null || String(v).trim() === "") return "";
  return String(v);
});

/** 报告已带服务端保存的 AI 分析时，只展示 analysis，不走流式解析与保存 */
const skipTeacherAiStream = computed(() => {
  const r = report.value;
  if (!r) return false;
  const a = r.analysis;
  return a !== undefined && a !== null && String(a).trim() !== "";
});

/** 精准报告里供 AI 解析的 prompt 原文（接口字段 aiSubmitContent，字符串） */
const aiSubmitContent = computed(() => {
  const raw = report.value?.aiSubmitContent;
  if (raw === undefined || raw === null) return "";
  const s = typeof raw === "string" ? raw : String(raw);
  return s.trim() === "" ? "" : s;
});

/** getPreciseReport 返回的报告 id，用于保存 AI 分析 */
const preciseReportId = computed(() => {
  const r = report.value;
  if (!r) return "";
  const id = r.reportId ?? r.id;
  if (id === undefined || id === null) return "";
  const s = typeof id === "string" ? id : String(id);
  return s.trim();
});

async function loadPreciseReport() {
  try {
    const res = await getPreciseReport();
    const vo = res?.data ?? null;
    if (vo) report.value = vo;
  } catch (error) {
    console.error("获取精准测评报告失败:", error);
  }
}

async function ensureAiToken() {
  try {
    const authRes = await aiAuth();
    if ((authRes.code === 200 || authRes.code === 0) && authRes.data != null && authRes.data !== "") {
      aiToken.value =
        typeof authRes.data === "string" ? authRes.data : String(authRes.data);
    } else {
      console.warn("AI 认证未返回可用 token:", authRes);
    }
  } catch (e) {
    console.error("AI 认证失败:", e);
  }
}

onMounted(async () => {
  setDesignSize(960, 600);
  await loadPreciseReport();
  if (!skipTeacherAiStream.value) {
    await ensureAiToken();
  }
});

onBeforeUnmount(() => {
  window.dispatchEvent(new Event("resize"));
});
</script>

<style scoped lang="scss">
.page {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100%;
  height: 100%;
  overflow-y: auto;
  background-color: #f8f8f8;
  padding-bottom: 20px;

  .header {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    flex: 0 0 160px;
    width: 960px;
    height: 160px;
    background: url("@/assets/report/header.png") no-repeat center top;
    background-size: contain;
    padding-top: 42px;
    box-sizing: border-box;

    .header-title {
      font-weight: bold;
      font-size: 19px;
      color: #ffffff;
    }

    .tips {
      font-size: 15px;
      color: #ffffff;
      margin-top: 7px;
    }
  }

  .back {
    position: absolute;
    left: 20px;
    top: 57px;
    width: 34px;
    height: 34px;
    img {
      width: 100%;
      height: 100%;
    }
  }

  .share {
    position: absolute;
    right: 0px;
    top: 57px;
    width: 77px;
    height: 30px;
    img {
      width: 100%;
      height: 100%;
    }
  }

  .box {
    position: relative;
    margin-top: -51px;
    flex: 0 0 auto;
    width: 920px;
    background: #ffffff;
    border-radius: 20px 20px 20px 20px;
    padding: 30px 20px;
    box-sizing: border-box;
  }
}
</style>
